import { useState, useEffect, useCallback } from 'react';
import { monitoringService, type IntegrationMetrics } from '../services/monitoring';

interface IntegrationHealth {
  integrations: Record<string, {
    health_score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
    last_success: string | null;
    failure_rate: number;
    non_success_rate?: number;
    server_error_rate?: number;
    avg_response_time: number;
  }>;
  overall_health: {
    score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
  };
  window_hours: number;
  window_label: string;
  last_updated: string;
}

interface Alert {
  type: string;
  source: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  failure_rate?: number;
  threshold?: number;
  avg_time_ms?: number;
}

interface AlertsResponse {
  alerts: Alert[];
  total_count: number;
  severity_breakdown: Record<string, number>;
}

interface MonitoringDashboard {
  integration_metrics: IntegrationMetrics;
  integration_health: IntegrationHealth;
  alerts: AlertsResponse;
  system_status: {
    monitoring_active: boolean;
    last_updated: string;
    data_retention_days: number;
    alert_count: number;
    integrations_monitored: number;
  };
}

interface UseMonitoringReturn {
  integrationMetrics: IntegrationMetrics | null;
  integrationHealth: IntegrationHealth | null;
  alerts: AlertsResponse | null;
  systemStatus: MonitoringDashboard['system_status'] | null;
  dashboard: MonitoringDashboard | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  fetchIntegrationMetrics: (integrationType?: string, hours?: number) => Promise<IntegrationMetrics>;
  fetchIntegrationHealth: () => Promise<IntegrationHealth>;
  fetchAlerts: (severity?: string, limit?: number) => Promise<AlertsResponse>;
}

export const useMonitoring = (
  timeWindow: number = 24,
  spaceId?: string
): UseMonitoringReturn => {
  const [integrationMetrics, setIntegrationMetrics] = useState<IntegrationMetrics | null>(null);
  const [integrationHealth, setIntegrationHealth] = useState<IntegrationHealth | null>(null);
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [systemStatus, setSystemStatus] = useState<MonitoringDashboard['system_status'] | null>(null);
  const [dashboard, setDashboard] = useState<MonitoringDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchIntegrationMetrics = useCallback(async (integrationType?: string, hours: number = timeWindow): Promise<IntegrationMetrics> => {
    if (!spaceId) {
      throw new Error('space_id is required for monitoring');
    }
    try {
      const metrics = await monitoringService.getIntegrationMetrics(integrationType, hours, spaceId);
      setIntegrationMetrics(metrics);
      return metrics;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch integration metrics';
      setError(errorMessage);
      throw err;
    }
  }, [timeWindow, spaceId]);

  const fetchIntegrationHealth = useCallback(async (): Promise<IntegrationHealth> => {
    if (!spaceId) {
      throw new Error('space_id is required for monitoring');
    }
    try {
      const health = await monitoringService.getIntegrationHealth(spaceId);
      setIntegrationHealth(health);
      return health;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch integration health';
      setError(errorMessage);
      throw err;
    }
  }, [spaceId]);

  const fetchAlerts = useCallback(async (severity?: string, limit: number = 50): Promise<AlertsResponse> => {
    if (!spaceId) {
      throw new Error('space_id is required for monitoring');
    }
    try {
      const alertsData = await monitoringService.getAlerts(severity, limit, spaceId);
      setAlerts(alertsData);
      return alertsData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch alerts';
      setError(errorMessage);
      throw err;
    }
  }, [spaceId]);

  const fetchDashboard = useCallback(async (hours: number = timeWindow): Promise<MonitoringDashboard | null> => {
    if (hours < 1 || !spaceId) {
      return null;
    }
    try {
      const dashboardData = await monitoringService.getDashboard(hours, spaceId);
      setDashboard(dashboardData);
      setIntegrationMetrics(dashboardData.integration_metrics);
      setIntegrationHealth(dashboardData.integration_health);
      setAlerts(dashboardData.alerts);
      setSystemStatus(dashboardData.system_status);
      return dashboardData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard data';
      setError(errorMessage);
      throw err;
    }
  }, [timeWindow, spaceId]);

  const refetch = useCallback(async (): Promise<void> => {
    if (timeWindow < 1 || !spaceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      await fetchDashboard(timeWindow);
    } catch (err) {
      console.error('Failed to refetch monitoring data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchDashboard, timeWindow, spaceId]);

  useEffect(() => {
    if (timeWindow > 0 && spaceId) {
      void refetch();
    } else {
      setIsLoading(false);
    }
  }, [refetch, timeWindow, spaceId]);

  return {
    integrationMetrics,
    integrationHealth,
    alerts,
    systemStatus,
    dashboard,
    isLoading,
    error,
    refetch,
    fetchIntegrationMetrics,
    fetchIntegrationHealth,
    fetchAlerts,
  };
};

/** @deprecated Prefer fetchIntegrationMetrics — same data, integration_type filter. */
export const useWebhookSourceMonitoring = (
  source: string,
  timeWindow: number = 24,
  spaceId?: string
) => {
  const [metrics, setMetrics] = useState<IntegrationMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!spaceId) {
      setMetrics(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const data = await monitoringService.getIntegrationMetrics(source, timeWindow, spaceId);
      setMetrics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch integration type metrics';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [source, timeWindow, spaceId]);

  useEffect(() => {
    void fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    isLoading,
    error,
    refetch: fetchMetrics,
  };
};

export const useIntegrationHealthMonitoring = (spaceId?: string) => {
  const [health, setHealth] = useState<IntegrationHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    if (!spaceId) {
      setHealth(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const data = await monitoringService.getIntegrationHealth(spaceId);
      setHealth(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch integration health';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    void fetchHealth();
  }, [fetchHealth]);

  return {
    health,
    isLoading,
    error,
    refetch: fetchHealth,
  };
};

export const useAlertsMonitoring = (
  severity?: string,
  limit: number = 50,
  spaceId?: string
) => {
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    if (!spaceId) {
      setAlerts(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const data = await monitoringService.getAlerts(severity, limit, spaceId);
      setAlerts(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch alerts';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [severity, limit, spaceId]);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts]);

  return {
    alerts,
    isLoading,
    error,
    refetch: fetchAlerts,
  };
};

export default useMonitoring;
